import React, { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router';

import { DataTableProvider } from '@/shared/components/ui/data-table/context/data-table-context';
import { TData } from '@/shared/components/ui/data-table/context/data-table-context.types';
import { InnerDataTable } from '@/shared/components/ui/data-table/variant/inner-data-table/inner-data-table';
import {
  FilterScopeProvider,
  useFilterScope,
} from '@/shared/features/filter-preferences/context/filter-scope-context';
import { useGroupsConfig } from '@/shared/features/groups/context/groups-config-context';
import { useGroupedResourceQueryParams } from '@/shared/features/groups/hooks/use-grouped-resource-query-params/use-grouped-resource-query-params';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { useGroupUsersListDefinitions } from '@/shared/features/rbac/hooks/use-users-list-definitions/use-users-list-definitions';
import { useUsersTransformer } from '@/shared/features/rbac/hooks/use-users-transformer/use-users-transformer';
import { TTransformedUser } from '@/shared/features/rbac/hooks/use-users-transformer/use-users-transformer.types';
import { UserFilterBuilder } from '@/shared/filters/entity-filters/user-filter-builder/user-filter-builder';
import { useReadAllUsersQuery } from '@/shared/redux/api/ident/ident-api';

const GroupUsersListInner = () => {
  const { isReady } = useFilterScope();
  const { groupId } = useParams();
  const { useTenantSettings, permissionActions: rbacPermissionActions } =
    useRbacConfig();
  const { ssoEnabled } = useTenantSettings();
  const {
    resourceLists,
    groupRoutePath,
    permissionActions: groupActions,
  } = useGroupsConfig();
  const listConfig = resourceLists?.users;
  const { can } = usePermissionCheck();
  const { pagination, filter } = useGroupedResourceQueryParams(
    groupId,
    new UserFilterBuilder(),
  );
  const { data, isLoading, isFetching } = useReadAllUsersQuery(
    { filter, pagination },
    { skip: !isReady },
  );

  const colDefs = useGroupUsersListDefinitions({
    ssoEnabled,
    actionsRenderer: listConfig?.rowActions,
  });
  const { transformUser } = useUsersTransformer();

  const rowData = data?.readAllUsers.items.map(transformUser);
  const canManageGroup = can(groupActions.updateGroup, groupId);
  const canUpdateUser = (item: TTransformedUser) =>
    can(rbacPermissionActions.updateUser, item.userId);
  const getItemId = (item: TTransformedUser) => item.userId;
  const getItemLink = (item: TTransformedUser) =>
    listConfig?.getItemLink?.(groupId ?? '', item.userId) ??
    `${groupRoutePath}/${groupId}/users/${item.userId}`;
  const disableItemCheckbox = (item: TTransformedUser) => !canUpdateUser(item);

  const getBatchActions = () => {
    const render = listConfig?.renderBatchActions;
    if (!canManageGroup || !render || !rowData?.some(canUpdateUser)) {
      return undefined;
    }

    return (
      selectedItems: TTransformedUser[],
      setSelectedItems: Dispatch<SetStateAction<TTransformedUser[]>>,
    ) => {
      if (
        !can(rbacPermissionActions.updateUser, selectedItems.map(getItemId))
      ) {
        return null;
      }

      return render(
        selectedItems.map(({ raw }) => raw),
        () => setSelectedItems([]),
      );
    };
  };

  const _data: TData<TTransformedUser> = {
    isFetching,
    isLoading,
    items: rowData || [],
    pages: data?.readAllUsers.pages ?? 0,
    itemCount: data?.readAllUsers.itemCount ?? 0,
  };

  return (
    <DataTableProvider<TTransformedUser>
      value={{ data: _data, getItemId, getItemLink, disableItemCheckbox }}
      colDefs={colDefs}
    >
      <InnerDataTable<TTransformedUser> batchActions={getBatchActions()} />
    </DataTableProvider>
  );
};

export const GroupUsersList = () => (
  <FilterScopeProvider scopeKey="group.users.list">
    <GroupUsersListInner />
  </FilterScopeProvider>
);
